import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { messagesApi } from '../api/messagesApi';
import type { SmsConversation, SmsConversationAction } from '../api/types';
import { findConversationByPhone } from '../adapters/messagesAdapters';

export function usePatchConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      action,
    }: {
      conversationId: string;
      action: SmsConversationAction;
    }) => messagesApi.patchConversation(conversationId, action),
    onSuccess: (_data, { conversationId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.sms.inbox });
      void queryClient.invalidateQueries({ queryKey: keys.sms.thread(conversationId) });
    },
  });
}

export function useSmsReply(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => messagesApi.sendReply(conversationId, text),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.sms.inbox });
      void queryClient.invalidateQueries({ queryKey: keys.sms.thread(conversationId) });
    },
  });
}

export function useGenerateSummary(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (episodeId: number) => messagesApi.generateEpisodeSummary(episodeId),
    onSuccess: () => {
      // AI Recep writes the summary asynchronously; refetch the thread to pick it up.
      void queryClient.invalidateQueries({ queryKey: keys.sms.thread(conversationId) });
    },
  });
}

export function useEscalate(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => messagesApi.escalate(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.sms.inbox });
      void queryClient.invalidateQueries({ queryKey: keys.sms.thread(conversationId) });
    },
  });
}

export interface ComposeSendInput {
  phone: string;
  text: string;
  patientId?: number;
  conversations: SmsConversation[];
}

export function useSmsComposeSend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ phone, text, patientId, conversations }: ComposeSendInput) => {
      const existing = findConversationByPhone(conversations, phone);
      if (existing) {
        await messagesApi.sendReply(existing.id, text);
        return existing.id;
      }

      const created = await messagesApi.sendOutbound({
        body: text,
        to_phone: phone,
        ...(patientId != null ? { patient_id: patientId } : {}),
      });
      return created.id ?? created.conversation_id ?? '';
    },
    onSuccess: (conversationId) => {
      void queryClient.invalidateQueries({ queryKey: keys.sms.inbox });
      if (conversationId) {
        void queryClient.invalidateQueries({ queryKey: keys.sms.thread(conversationId) });
      }
    },
  });
}
